import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { Row, Col, Card, Alert, Button } from 'reactstrap'
import SellerMarketProductMainAttributeForm from 'domains/seller-market-product/organisms/main-attribute-form'
import SellerMarketProductVariantAttributeForm from 'domains/seller-market-product/organisms/variant-attribute-form'
import MarketProductsApiV2 from 'api/v2/admins/seller-market-products-api-v2'
import { CODE, CONTEXT } from 'domains/product-attribute/constants'
import LoaderButton from 'components/molecules/loader-button'
import ConfirmationModal from 'components/organisms/confirmation-modal'
import { useModal } from 'providers/modal-provider'
import { useParams } from 'react-router'
import PropTypes from 'prop-types'
import TitlePage from 'components/atoms/title-page'
import { LIVE_STATUS } from 'domains/seller-market-product/constants'
import { ALERT_TYPES } from 'constants/alert-constants'
import { CardTextData } from 'components/molecules'
import { SellerMarketProductStatusBadge } from 'domains/seller-market-product/atoms'
import { useHistory } from 'react-router'
import ProductAttributesApiV2 from 'api/v2/admins/product-attributes-api-v2'
import ProductCategoriesApiV2 from 'api/v2/admins/product-categories-api-v2'
import { RedirectButton } from 'components/atoms'

const defaultFormData = {
  status            : '',
  mainAttributeForm : {
    initialValues: {
      [CODE.CATEGORY]  : '',
      [CODE.CONDITION] : '',
      [CODE.PRICE]     : '',
      [CODE.BRAND]     : ''
    },
    values: null
  },
  variantAttributeForm: {
    initialValues : {},
    values        : null
  }
}

const propTypes = {
  pageUtils: PropTypes.shape({
    setAlertMsg    : PropTypes.func,
    setApiErrorMsg : PropTypes.func
  })
}

// * In Seller Curation form, we don't want to input quantity
const EXCLUDED_ATTRIBUTE_KEYS = [
  CODE.QUANTITY,
  CODE.CATEGORY,
  CODE.CONDITION,
  CODE.PRICE,
  CODE.BRAND
]

const MarketProductEditPage = ({ pageUtils }) => {
  const [ mainAttributes, setMainAttributes ]               = useState([])
  const [ measurementAttributes, setMeasurementAttributes ] = useState([])
  const [ productSizes, setProductSizes ]                   = useState([])
  const [ isLoading, setIsLoading ]                         = useState(true)
  const [ isAlreadyLived, setisAlreadyLived ]               = useState()
  const [ isSubmitting, setIsSubmitting ]                   = useState(false)
  const [ formData, setFormData ]                           = useState(defaultFormData)
  const { showModal, hideModal }                            = useModal()
  const { id }                                              = useParams()

  const mainAttributeFormRef    = useRef()
  const variantAttributeFormRef = useRef()
  const history                 = useHistory()

  /** handler to update new Seller Market Product. */
  const handleUpdateMarketProduct = useCallback(() => {
    const { mainAttributeForm, variantAttributeForm } = formData
    MarketProductsApiV2.update({
      id                     : id,
      mainAttributeValues    : mainAttributeForm.values,
      variantAttributeValues : variantAttributeForm.values
    })
      .then(() => pageUtils.setAlertMsg('Market product has been updated', ALERT_TYPES.SUCCESS))
      .catch((error) => pageUtils.setApiErrorMsg(error.response.data))
      .finally(() => {
        setIsSubmitting(false)
        hideModal()
      })
  }, [ formData, hideModal, id, pageUtils ])

  const handleFetchAttributes = useCallback(async (category_id) => {
    try {
      // Get product attribute
      const productAttributeResponse = await ProductAttributesApiV2.getCurationAttributes(category_id)
      // exclude particular attributes
      const filteredAttributes = productAttributeResponse.data.attributes.filter(
        (attribute) => !EXCLUDED_ATTRIBUTE_KEYS.includes(attribute.code)
      )
      setMainAttributes(
        filteredAttributes.filter(
          (attribute) => attribute.context !== CONTEXT.MEASUREMENT
        )
      )
      setMeasurementAttributes(
        filteredAttributes.filter(
          (attribute) => attribute.context === CONTEXT.MEASUREMENT
        )
      )

      // Get product size
      const gender              = formData.mainAttributeForm.initialValues.gender
      const productSizeResponse = await ProductCategoriesApiV2.getSizes(category_id, gender)
      setProductSizes(productSizeResponse.data.sizes)

      setIsLoading(false)

      // trigger to reload attribute forms
      mainAttributeFormRef.current.reload()
      variantAttributeFormRef.current.reload()
    }
    catch (error) {
      console.log(error)
    }
  }, [])

  useEffect(() => {
    /** Get field values and attributes from the backend */
    MarketProductsApiV2.edit(id)
      .then((response) => {
        const { live_status, process_status, data, structure } = response.data
        const { dimensions, ...main }                          = data

        setFormData((prev) => ({
          process_status    : process_status,
          mainAttributeForm : {
            ...prev.mainAttributeForm,
            initialValues: main
          },
          variantAttributeForm: {
            ...prev.variantAttributeForm,
            initialValues: { variants: dimensions }
          }
        }))

        setisAlreadyLived(LIVE_STATUS.LIVE === live_status)
        setMainAttributes(structure.main)
        setMeasurementAttributes(structure.dimensions)
        setProductSizes(structure.product_sizes)

        setIsLoading(false)
      })
  }, [ id ])

  useEffect(() => {
    if (!isLoading) {
      mainAttributeFormRef.current.reload()
      variantAttributeFormRef.current.reload()
    }
  }, [ isLoading ])

  useEffect(() => {
    if (isSubmitting) {
      showModal(ConfirmationModal, {
        title    : 'Product Confirmation',
        children : 'Apakah kamu yakin?',
        toggle   : () => {
          hideModal()
          setIsSubmitting(false)
        },
        handleConfirmation: async () => handleUpdateMarketProduct()
      })
    }
  }, [ handleUpdateMarketProduct, hideModal, isSubmitting, showModal ])

  const handleSubmit = async () => {
    await mainAttributeFormRef.current.submit()
    // set the submit button if variant attribute is showed
    await variantAttributeFormRef.current?.submit()

    if (isAllFormValid()) setIsSubmitting(true)
  }

  /** A flag whether all form is valid or not */
  const isAllFormValid = () => mainAttributeFormRef.current.isValid && variantAttributeFormRef.current.isValid

  /** Handler to update formData.mainAttributeForm */
  const handleChangeMainAttributeForm = (data) => setFormData((prev) => ({
    ...prev,
    mainAttributeForm: {
      ...prev.mainAttributeForm,
      ...data
    }
  }))

  /** Handler to update formData.variantAttributeForm */
  const handleChangeVariantAttributeForm = (data) => setFormData((prev) => ({
    ...prev,
    variantAttributeForm: {
      ...prev.variantAttributeForm,
      ...data
    }
  }))


  /**
   * memoized disabled field for particular field
   * note: we disable some field depending on whether the product is already lived or not
   */
  const memoizedDisabledMainFields = useMemo(() => ({
    category : true,
    price    : true
  }), [])

  /**
   * memoized disabled field for particular field
   * note: we disable some field depending on whether the product is already lived or not
   */
  const memoizedDisabledVariantFields = useMemo(() => ({
    variants: {
      size          : isAlreadyLived,
      quantity      : isAlreadyLived,
      removeVariant : isAlreadyLived
    },
    button: isAlreadyLived
  }), [ isAlreadyLived ])

  const handleClickPhotoUpload = () => history.push(`/app/sellers-market/inbounds/${id}/photo-upload`)

  const handleClickPhotoEdit = () => history.push(`/app/sellers-market/inbounds/${id}/photo-edit`)

  const isProductSizeEmpty = productSizes.length === 0

  return (
    <>
      <RedirectButton
        className={ 'mb-3' }
        color='primary'
        size='sm'
        text={ '< Back to Review' }
        path={ `/app/sellers-market/${id}/review` }
      />

      <TitlePage mainTitle={ 'Seller Market Product' } subTitle={ 'Edit Page' } />

      <div className='d-flex justify-content-between mb-3'>
        <div className='d-flex align-items-end'>
          <Button
            color='primary'
            size='sm'
            onClick={ handleClickPhotoEdit }
          >
            { 'Photo Edit' }
          </Button>

          <Button
            color='primary'
            size='sm'
            onClick={ handleClickPhotoUpload }
          >
            { 'Photo Upload' }
          </Button>
        </div>

        <CardTextData
          label={ 'Process Status' }
          value={
            <SellerMarketProductStatusBadge
              status={ formData.process_status }
            />
          }
        />
      </div>

      <Row>
        <Col
          xs="12"
          sm="12"
          md="6"
          lg="6"
        >
          <Card body>
            <h3>{ 'Detail Produk' }</h3>
            <Alert color="info">
              { 'kategori dan harga tidak bisa kamu ubah setelah produknya dibuat oleh Seller' }
              <br /><br />
              { 'Untuk incomplete status (market product yang terbuat dari fast sell product) masih dapat mengubah seller dan kategori' }
            </Alert>

            <SellerMarketProductMainAttributeForm
              refId={ mainAttributeFormRef }
              isLoading={ isLoading }
              attributes={ mainAttributes }
              onChange={ handleChangeMainAttributeForm }
              formData={ formData }
              disabledFields={ memoizedDisabledMainFields }
              onFetchAttributes={ handleFetchAttributes }
            />
          </Card>
        </Col>

        <Col
          xs="12"
          sm="12"
          md="6"
          lg="6"
        >
          <Card body>
            <h3>{ 'Variant' }</h3>
            <Alert color="info">
              { 'Produk yang sudah live tidak bisa diubah ukuran dan kuantitasnya' }
            </Alert>
            {
              isProductSizeEmpty && (
                <small>
                  { 'Karena produk ini tidak memiliki pilihan ukuran, maka variannya akan dianggap satu' }
                </small>
              )
            }
          </Card>

          <Card body>
            <h3 className='mb-3'>{ 'Ukuran' }</h3>

            <SellerMarketProductVariantAttributeForm
              formRef={ variantAttributeFormRef }
              isLoading={ isLoading }
              onChange={ handleChangeVariantAttributeForm }
              attributes={ measurementAttributes }
              disabledFields={ memoizedDisabledVariantFields }
              initialValues={ formData.variantAttributeForm.initialValues }
              productSizes={ productSizes }
            />
          </Card>
        </Col>
      </Row>

      <LoaderButton
        className="float-right"
        type="submit"
        onClick={ handleSubmit }
        color="primary"
        disabled={ isSubmitting }
        isLoading={ isSubmitting }
        text={ 'Simpan' }
      />
    </>
  )
}

MarketProductEditPage.propTypes = propTypes

export default MarketProductEditPage
